import React, { useState, useCallback, useRef, useEffect } from 'react';
import fileDownload from 'js-file-download';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { toSlug } from '../../shared/utils';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/* Share the current page URL — shown in the desktop toolbar */
function DesktopShareButton() {
  const [status, setStatus] = useState('idle');

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ url }); } catch { /* cancelled */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setStatus('copied');
      setTimeout(() => setStatus('idle'), 2000);
    } catch {
      setStatus('unsupported');
      setTimeout(() => setStatus('idle'), 2500);
    }
  }, []);

  const label = status === 'copied' ? 'Copied!' : status === 'unsupported' ? 'Failed' : 'Share';
  const icon  = status === 'copied' ? 'bi-check2' : navigator.share ? 'bi-share' : 'bi-link-45deg';

  return (
    <button
      type="button"
      className={`doc-action-btn doc-action-btn--ghost share-btn--desktop${status !== 'idle' ? ` share-btn--${status}` : ''}`}
      onClick={handleShare}
      aria-label={label}
      aria-live="polite"
    >
      <i className={`bi ${icon}`} aria-hidden="true"></i>
      <span>{label}</span>
    </button>
  );
}

export function DocumentViewer({ transaction }) {
  const details      = transaction?.transactionDetails;
  const documentUrl  = details?.documentDetails?.document;
  const [numPages, setNumPages]       = useState(null);
  const [pageWidth, setPageWidth]     = useState(700);
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      if (canvasRef.current) {
        const w = canvasRef.current.clientWidth - 32;
        setPageWidth(Math.max(280, Math.min(w, 960)));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, []);

  const fileName =
    [
      toSlug(transaction?.receiver?.CompanyName),
      toSlug(details?.transactionType),
      details?.EntryNo,
    ]
      .filter(Boolean)
      .join('-') + '.pdf';

  const handleLoadSuccess = useCallback(({ numPages: n }) => setNumPages(n), []);

  const handleDownload = useCallback(async () => {
    if (!documentUrl || downloading) return;
    setDownloading(true);
    setDownloadErr(null);
    try {
      const res = await axios.get(documentUrl, { responseType: 'blob' });
      fileDownload(res.data, fileName);
    } catch (err) {
      setDownloadErr('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [documentUrl, fileName, downloading]);

  return (
    <div className="doc-viewer">
      <div className="doc-toolbar">
        <div className="doc-toolbar-left">
          <div className="doc-type-badge">
            <i className="bi bi-file-earmark-pdf" aria-hidden="true"></i>
            <span>{details?.transactionType || 'Document'}</span>
          </div>
          {numPages !== null && (
            <span className="doc-page-count">
              {numPages} {numPages === 1 ? 'page' : 'pages'}
            </span>
          )}
        </div>

        <div className="doc-actions">
          {documentUrl && (
            <a
              href={documentUrl}
              target="_blank"
              rel="noreferrer"
              className="doc-action-btn doc-action-btn--ghost"
              aria-label="Open document fullscreen"
            >
              <i className="bi bi-arrows-fullscreen" aria-hidden="true"></i>
              <span>Fullscreen</span>
            </a>
          )}
          <DesktopShareButton />
          <button
            type="button"
            className={`doc-action-btn doc-action-btn--primary${downloading ? ' is-loading' : ''}`}
            aria-label={downloading ? 'Downloading…' : 'Download document'}
            onClick={handleDownload}
            disabled={!documentUrl || downloading}
          >
            {downloading
              ? <><i className="bi bi-hourglass-split" aria-hidden="true"></i><span>Downloading…</span></>
              : <><i className="bi bi-download" aria-hidden="true"></i><span>Download</span></>
            }
          </button>
        </div>
      </div>

      {downloadErr && (
        <div className="doc-error-bar" role="alert">
          <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
          {downloadErr}
          <button className="doc-error-dismiss" onClick={() => setDownloadErr(null)} aria-label="Dismiss">
            <i className="bi bi-x" aria-hidden="true"></i>
          </button>
        </div>
      )}

      <div className="doc-canvas" ref={canvasRef}>
        {documentUrl ? (
          <Document
            file={documentUrl}
            onLoadSuccess={handleLoadSuccess}
            loading={
              <div className="pdf-loading">
                <i className="bi bi-hourglass-split" aria-hidden="true"></i>
                Loading document…
              </div>
            }
            error={
              <div className="doc-placeholder">
                <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
                <p>Failed to load document.</p>
                <a href={documentUrl} target="_blank" rel="noreferrer" className="doc-action-btn doc-action-btn--ghost">
                  Open directly
                </a>
              </div>
            }
          >
            {Array.from({ length: numPages ?? 0 }, (_, i) => (
              <Page
                key={`page_${i + 1}`}
                pageNumber={i + 1}
                width={pageWidth}
                renderTextLayer
                renderAnnotationLayer
              />
            ))}
          </Document>
        ) : (
          <div className="doc-placeholder">
            <i className="bi bi-file-earmark-x" aria-hidden="true"></i>
            <p>No document available.</p>
          </div>
        )}
      </div>
    </div>
  );
}
